import { type FieldErrors, type UseFormRegister } from "react-hook-form";

import { Input } from "~/ui";
import { FieldError } from "~/ui/common/FieldError";
import { type ExistingUser } from "~/utils";
import { type AddUserFormData } from "../add-user-schema";

interface AddUserFieldsProps {
  register: UseFormRegister<AddUserFormData>;
  errors: FieldErrors<AddUserFormData>;
  existingUser: ExistingUser | null;
  isSubmitting: boolean;
  onOpenExistingUser: (userId: ExistingUser["id"]) => void;
}

export const AddUserFields = ({
  register,
  errors,
  existingUser,
  isSubmitting,
  onOpenExistingUser,
}: AddUserFieldsProps) => {
  const firstNameError = errors.firstName?.message;
  const lastNameError = errors.lastName?.message;
  const emailError =
    errors.email?.message ??
    (existingUser ? "An account already uses this email address." : undefined);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
        Account information
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Input
            id="add-user-first-name"
            label="First name"
            placeholder="E.g. Jane"
            required
            disabled={isSubmitting}
            isError={Boolean(firstNameError)}
            aria-describedby={
              firstNameError ? "add-user-first-name-error" : undefined
            }
            {...register("firstName")}
          />
          {firstNameError && (
            <FieldError id="add-user-first-name-error">
              {firstNameError}
            </FieldError>
          )}
        </div>

        <div>
          <Input
            id="add-user-last-name"
            label="Last name"
            placeholder="E.g. Smith"
            required
            disabled={isSubmitting}
            isError={Boolean(lastNameError)}
            aria-describedby={
              lastNameError ? "add-user-last-name-error" : undefined
            }
            {...register("lastName")}
          />
          {lastNameError && (
            <FieldError id="add-user-last-name-error">
              {lastNameError}
            </FieldError>
          )}
        </div>
      </div>

      <div>
        <Input
          id="add-user-email"
          label="Email address"
          inputType="email"
          placeholder="jane.smith@example.com"
          required
          disabled={isSubmitting}
          isError={Boolean(emailError)}
          aria-describedby={emailError ? "add-user-email-error" : undefined}
          {...register("email")}
        />
        {emailError && (
          <FieldError id="add-user-email-error">{emailError}</FieldError>
        )}
        {existingUser && (
          <button
            type="button"
            className="text-violet pt-2 text-sm underline"
            onClick={() => onOpenExistingUser(existingUser.id)}
          >
            {`Open ${existingUser.firstName} ${existingUser.lastName} in User Details`}
          </button>
        )}
      </div>
    </div>
  );
};
